import { parseCsv } from "./mcEngineData";

const DB_NAME = "matrixos-morpheus-imports";
const DB_VERSION = 3;
const STORE_NAME = "activeSnapshots";
const WINDOW_CACHE_KEY = "__MC_ACTIVE_IMPORT_SNAPSHOTS__";
const SESSION_CACHE_PREFIX = "mc-active-import-route:";

// Tenant isolation: every browser cache key must include the active organisation.
// The previous scope-only cache allowed an ALL-sites snapshot from one business
// to be reused by another business in the same browser on production.
function activeOrgKey() {
  if (typeof window === "undefined") return "local";
  try {
    const value = String(window.sessionStorage?.getItem("mc-active-org-id") || "").trim();
    return value || "local";
  } catch (_) {
    return "local";
  }
}

function tenantCacheKey(scope = "ALL") {
  return `${activeOrgKey()}::${normalizeScope(scope)}`;
}

function snapshotBelongsToActiveOrg(snapshot = null) {
  if (!snapshot || typeof snapshot !== "object") return false;
  const active = activeOrgKey();
  const stored = String(snapshot.__mcOrgId || snapshot.orgId || "").trim();
  return Boolean(stored && stored === active);
}


function normalizeScope(scope = "ALL") {
  const raw = String(scope || "ALL").trim();
  if (!raw || raw.toUpperCase() === "ALL") return "ALL";
  const withoutPrefix = raw.replace(/^inventory_/i, "");
  const cleaned = withoutPrefix
    .replace(/_Store$/i, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!cleaned) return "ALL";
  const titled = cleaned
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("_");
  return `inventory_${titled}`;
}

function scopeCandidates(scope = "ALL") {
  const raw = String(scope || "ALL").trim() || "ALL";
  const normalized = normalizeScope(raw);
  const label = normalized === "ALL"
    ? "ALL"
    : normalized.replace(/^inventory_/i, "").replace(/_/g, " ");
  return Array.from(new Set([
    normalized,
    raw,
    label,
    raw.toLowerCase(),
    normalized.toLowerCase(),
    label.toLowerCase(),
  ].filter(Boolean)));
}

function sessionCacheKey(scope = "ALL") {
  return `${SESSION_CACHE_PREFIX}${tenantCacheKey(scope)}`;
}

function compactForRoute(snapshot = {}) {
  if (!snapshot || typeof snapshot !== "object") return null;
  // CSV text is dramatically smaller than keeping both 72-column raw rows and
  // a second prepared-row copy. Persist the exact source text and reconstruct
  // rows on the consuming route. POS/cloud snapshots without csvText retain
  // their raw rows.
  if (typeof snapshot.csvText === "string" && snapshot.csvText.length) {
    const { rawRows, preparedRows, ...rest } = snapshot;
    return { ...rest, csvText: snapshot.csvText, rawRows: [], preparedRows: [] };
  }
  return snapshot;
}

function hydrateRouteSnapshot(snapshot = null) {
  if (!snapshot || typeof snapshot !== "object") return null;
  if (Array.isArray(snapshot.rawRows) && snapshot.rawRows.length) return snapshot;
  if (typeof snapshot.csvText !== "string" || !snapshot.csvText.trim()) return snapshot;
  try {
    const parsed = parseCsv(snapshot.csvText);
    return {
      ...snapshot,
      parsedHeaders: Array.isArray(snapshot.parsedHeaders) && snapshot.parsedHeaders.length
        ? snapshot.parsedHeaders
        : parsed.headers,
      rawRows: parsed.rows,
      rowCount: Number(snapshot.rowCount || parsed.rows.length),
      columnCount: Number(snapshot.columnCount || parsed.headers.length),
    };
  } catch (_) {
    return snapshot;
  }
}

function saveSessionRouteSnapshot(scope, snapshot) {
  if (typeof window === "undefined" || !window.sessionStorage || !snapshot) return false;
  try {
    window.sessionStorage.setItem(sessionCacheKey(scope), JSON.stringify(compactForRoute(snapshot)));
    return true;
  } catch (_) {
    return false;
  }
}

function loadSessionRouteSnapshot(scope) {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(sessionCacheKey(scope));
    return raw ? hydrateRouteSnapshot(JSON.parse(raw)) : null;
  } catch (_) {
    return null;
  }
}

function getWindowCache() {
  if (typeof window === "undefined") return null;
  if (!window[WINDOW_CACHE_KEY]) window[WINDOW_CACHE_KEY] = Object.create(null);
  return window[WINDOW_CACHE_KEY];
}

function rememberInMemory(scope, snapshot) {
  const cache = getWindowCache();
  if (!cache || !snapshot) return;
  const hydrated = hydrateRouteSnapshot(snapshot);
  const org = activeOrgKey();
  scopeCandidates(scope).forEach((key) => { cache[`${org}::${key}`] = hydrated; });
}

export function peekActiveImportSnapshotCache(scope = "ALL") {
  const cache = getWindowCache();
  if (cache) {
    const org = activeOrgKey();
    for (const key of scopeCandidates(scope)) {
      const value = cache[`${org}::${key}`];
      if (value && snapshotBelongsToActiveOrg(value)) return hydrateRouteSnapshot(value);
    }
  }
  const sessionSnapshot = loadSessionRouteSnapshot(scope);
  if (sessionSnapshot && snapshotBelongsToActiveOrg(sessionSnapshot)) {
    rememberInMemory(scope, sessionSnapshot);
    return sessionSnapshot;
  }
  return null;
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      resolve(null);
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "scope" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Unable to open import snapshot cache"));
  });
}

export async function saveActiveImportSnapshotCache(scope = "ALL", snapshot = {}) {
  const canonicalScope = normalizeScope(scope);
  const normalizedSnapshot = { ...snapshot, scope: canonicalScope, __mcOrgId: activeOrgKey() };
  const compactSnapshot = compactForRoute(normalizedSnapshot);

  // Route navigation inside the SPA must never depend on Firestore/IndexedDB
  // completing again. Keep the committed snapshot in a window-level handoff
  // cache immediately; IndexedDB remains the durable refresh cache.
  rememberInMemory(scope, normalizedSnapshot);
  saveSessionRouteSnapshot(scope, normalizedSnapshot);

  const db = await openDb().catch(() => null);
  if (!db) return false;
  const savedAt = new Date().toISOString();
  const org = activeOrgKey();
  const records = scopeCandidates(scope).map((candidate) => ({
    scope: `${org}::${candidate}`,
    canonicalScope,
    orgId: org,
    savedAt,
    snapshot: compactSnapshot,
  }));
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    records.forEach((record) => store.put(record));
    tx.oncomplete = () => { db.close(); resolve(true); };
    tx.onerror = () => { db.close(); resolve(false); };
    tx.onabort = () => { db.close(); resolve(false); };
  });
}

export async function loadActiveImportSnapshotCache(scope = "ALL") {
  const immediate = peekActiveImportSnapshotCache(scope);
  if (immediate) return immediate;

  const db = await openDb().catch(() => null);
  if (!db) return null;
  const org = activeOrgKey();
  const candidates = scopeCandidates(scope).map((candidate) => `${org}::${candidate}`);

  // Read aliases in separate short transactions. This avoids browser-specific
  // IndexedDB transaction auto-close behaviour while chaining requests.
  for (const candidate of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const record = await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get(candidate);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    });
    const snapshot = hydrateRouteSnapshot(record?.snapshot || null);
    if (snapshot && snapshotBelongsToActiveOrg(snapshot)) {
      rememberInMemory(scope, snapshot);
      saveSessionRouteSnapshot(scope, snapshot);
      db.close();
      return snapshot;
    }
  }

  db.close();
  return null;
}

export async function clearActiveImportSnapshotCache(scope = "ALL") {
  const cache = getWindowCache();
  if (cache) {
    const org = activeOrgKey();
    scopeCandidates(scope).forEach((key) => { delete cache[`${org}::${key}`]; });
  }
  if (typeof window !== "undefined" && window.sessionStorage) {
    try { window.sessionStorage.removeItem(sessionCacheKey(scope)); } catch (_) {}
  }

  const db = await openDb().catch(() => null);
  if (!db) return false;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const org = activeOrgKey();
    scopeCandidates(scope).forEach((candidate) => store.delete(`${org}::${candidate}`));
    tx.oncomplete = () => { db.close(); resolve(true); };
    tx.onerror = () => { db.close(); resolve(false); };
    tx.onabort = () => { db.close(); resolve(false); };
  });
}
